'use client';

import { useLocale } from '@/lib/i18n/use-locale';
import Link from 'next/link';
import { StatusBadge } from './status-badge';

export function Footer() {
  const currentYear = new Date().getFullYear();
  const { t } = useLocale();

  return (
    <footer className='mt-auto border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'>
      <div className='max-w-6xl mx-auto px-4 py-12'>
        {/* Main Footer Content */}
        <div className='grid grid-cols-2 md:grid-cols-4 gap-8 mb-8'>
          {/* Brand Section */}
          <div className='col-span-2 md:col-span-1'>
            <Link href='/' prefetch={false} className='inline-flex items-center gap-2 mb-4'>
              <span className='sr-only'>byt.la</span>
              <img
                src='/bytla_logo.svg'
                alt={t('nav.logoAlt')}
                className='h-8 w-auto dark:hidden'
              />
              <img
                src='/bytla_logo_dark.svg'
                alt={t('nav.logoAlt')}
                className='hidden h-8 w-auto dark:block'
              />
            </Link>
            <p className='text-sm text-gray-600 dark:text-gray-400 mb-4'>{t('footer.tagline')}</p>
            <div className='flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400'>
              <span>{t('footer.madeWith')}</span>
              <span className='text-red-500'>♥</span>
              <span>{t('footer.usingNext')}</span>
            </div>
          </div>

          {/* Product Links */}
          <div className='col-span-1'>
            <h3 className='font-semibold text-gray-900 dark:text-white mb-4'>
              {t('footer.product')}
            </h3>
            <ul className='space-y-3'>
              <li>
                <Link
                  href='/'
                  prefetch={false}
                  className='text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors'
                >
                  {t('footer.home')}
                </Link>
              </li>
              <li>
                <Link
                  href='/about'
                  prefetch={false}
                  className='text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors'
                >
                  {t('footer.about')}
                </Link>
              </li>
              <li>
                <Link
                  href='/faq'
                  prefetch={false}
                  className='text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors'
                >
                  {t('footer.faq')}
                </Link>
              </li>
              <li>
                <Link
                  href='/dashboard'
                  prefetch={false}
                  className='text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors'
                >
                  {t('footer.dashboard')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal Links */}
          <div className='col-span-1'>
            <h3 className='font-semibold text-gray-900 dark:text-white mb-4'>
              {t('footer.legal')}
            </h3>
            <ul className='space-y-3'>
              <li>
                <Link
                  href='/terms'
                  prefetch={false}
                  className='text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors'
                >
                  {t('footer.terms')}
                </Link>
              </li>
              <li>
                <Link
                  href='/privacy'
                  prefetch={false}
                  className='text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors'
                >
                  {t('footer.privacy')}
                </Link>
              </li>
              <li>
                <Link
                  href='/cookies'
                  prefetch={false}
                  className='text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors'
                >
                  {t('footer.cookies')}
                </Link>
              </li>
              <li>
                <Link
                  href='/abuse'
                  prefetch={false}
                  className='text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors'
                >
                  {t('footer.reportAbuse')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Support Links */}
          <div className='col-span-2 md:col-span-1'>
            <h3 className='font-semibold text-gray-900 dark:text-white mb-4'>
              {t('footer.support')}
            </h3>
            <ul className='space-y-3'>
              <li>
                <Link
                  href='/faq'
                  prefetch={false}
                  className='text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors'
                >
                  {t('footer.helpCenter')}
                </Link>
              </li>
              <li>
                <a
                  href='https://github.com'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors inline-flex items-center gap-1'
                >
                  {t('footer.github')}
                  <svg className='w-3 h-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'
                    />
                  </svg>
                </a>
              </li>
              <li>
                <Link
                  href='/abuse'
                  prefetch={false}
                  className='text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors'
                >
                  {t('footer.contactSupport')}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className='pt-8 border-t border-gray-200 dark:border-gray-700'>
          <div className='flex flex-col md:flex-row justify-between items-center gap-4'>
            {/* Copyright */}
            <p className='text-sm text-gray-500 dark:text-gray-400 text-center md:text-left'>
              © {currentYear} byt.la. {t('footer.rights')}
            </p>

            {/* Quick Links */}
            <div className='flex flex-wrap justify-center gap-4 text-xs text-gray-500 dark:text-gray-400'></div>

            {/* Status Badge */}
            <StatusBadge />
          </div>
        </div>
      </div>
    </footer>
  );
}
